import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface Toast {
    id: string;
    message: string;
    variant: ToastVariant;
    duration?: number;
    action?: ToastAction;
}

export interface ToastOptions {
    variant?: ToastVariant;
    duration?: number;
    action?: ToastAction;
}

interface ToastContextType {
    toasts: Toast[];
    addToast: (message: string, options?: ToastOptions) => string;
    removeToast: (id: string) => void;
    success: (message: string, options?: Omit<ToastOptions, 'variant'>) => string;
    error: (message: string, options?: Omit<ToastOptions, 'variant'>) => string;
    warning: (message: string, options?: Omit<ToastOptions, 'variant'>) => string;
    info: (message: string, options?: Omit<ToastOptions, 'variant'>) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

const DEFAULT_DURATION = 5000;

interface ToastProviderProps {
    children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timerRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

    const removeToast = useCallback((id: string) => {
        const timer = timerRefs.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timerRefs.current.delete(id);
        }
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const addToast = useCallback((message: string, options: ToastOptions = {}): string => {
        const id = crypto.randomUUID();
        const { variant = 'info', duration = DEFAULT_DURATION, action } = options;

        const toast: Toast = {
            id,
            message,
            variant,
            duration,
            action,
        };

        setToasts(prev => [...prev, toast]);

        if (duration > 0) {
            const timer = setTimeout(() => {
                removeToast(id);
            }, duration);
            timerRefs.current.set(id, timer);
        }

        return id;
    }, [removeToast]);

    const success = useCallback((message: string, options?: Omit<ToastOptions, 'variant'>) => {
        return addToast(message, { ...options, variant: 'success' });
    }, [addToast]);

    const error = useCallback((message: string, options?: Omit<ToastOptions, 'variant'>) => {
        return addToast(message, { ...options, variant: 'error' });
    }, [addToast]);

    const warning = useCallback((message: string, options?: Omit<ToastOptions, 'variant'>) => {
        return addToast(message, { ...options, variant: 'warning' });
    }, [addToast]);

    const info = useCallback((message: string, options?: Omit<ToastOptions, 'variant'>) => {
        return addToast(message, { ...options, variant: 'info' });
    }, [addToast]);

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
            {children}
        </ToastContext.Provider>
    );
};
